import { useCallback, useMemo, useRef } from 'react'

import { Flex, Tag } from 'antd'
import styled from 'styled-components'

import { UniversalSearchType } from 'src/components/UniversalSearchModal/util'
import useShowConditionsUi from 'src/hooks/use-show-conditions-ui'
import {
  useAmIReviewer,
  useMyOrg,
  useMyOrgAddressEntityLayerId,
  useMyOrgOtherEntityLayers,
  useMyRecordTypes,
} from 'src/layouts/AppStateContextLayout/utils'
import { KeyboardKey } from 'src/types'
import { sortEntityLayersForMapSwitcher } from 'src/utils/entityDisplayHelpers'
import { FeatureGate, useFeatureFlagging } from 'src/utils/featureFlagging/featureFlagging'

const StyledFlex = styled(Flex)`
  padding: 12px 0px;
`
const StyledTag = styled(Tag)`
  margin: 0;
  cursor: pointer;
`

type Props = {
  searchType: UniversalSearchType
  onSearchTypeChange: (searchType: UniversalSearchType) => void
}
const UniversalSearchTypeRadioGroup = ({ searchType, onSearchTypeChange }: Props) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const recordTypes = useMyRecordTypes()
  const hasCodeEnforcementModule = useMyOrg()?.hasCodeEnforcementModule
  const isReviewer = useAmIReviewer()
  const hasAddressLayer = !!useMyOrgAddressEntityLayerId()
  const otherEntityLayers = useMyOrgOtherEntityLayers()
  const showConditionsUi = useShowConditionsUi()
  const { checkFeatureFlag } = useFeatureFlagging()
  const showPlanPagesSearch = checkFeatureFlag(FeatureGate.OpenSearchUniversalSearchForRecords)

  const allSearchTypes: UniversalSearchType[] = useMemo(() => [{ label: 'All', mode: 'all' }], [])
  const recordSearchTypes: UniversalSearchType[] = useMemo(
    () =>
      recordTypes
        ?.filter((rt) => rt.shouldShowInStaffNavigation)
        .map((rt) => ({
          label: rt.name,
          mode: 'records',
          recordTypeId: rt.id,
        })) ?? [],
    [recordTypes]
  )
  const planPagesSearchTypes: UniversalSearchType[] = useMemo(
    () => (showPlanPagesSearch ? [{ label: 'Plan set pages', mode: 'planPages' }] : []),
    [showPlanPagesSearch]
  )
  const violationsSearchTypes: UniversalSearchType[] = useMemo(
    () =>
      hasCodeEnforcementModule && !isReviewer
        ? [
            {
              label: `Violations`,
              mode: 'violations',
            },
          ]
        : [],
    [hasCodeEnforcementModule, isReviewer]
  )
  const addressSearchTypes: UniversalSearchType[] = useMemo(
    () => [
      ...(hasAddressLayer
        ? [
            {
              label: 'Addresses',
              mode: 'addresses' as const,
            },
          ]
        : []),
      {
        label: 'Parcels',
        mode: 'parcels',
      },
      ...(showConditionsUi
        ? [
            {
              label: 'Conditions',
              mode: 'conditions' as const,
            },
          ]
        : []),
    ],
    [hasAddressLayer, showConditionsUi]
  )

  const otherLayerSearchTypes: UniversalSearchType[] = useMemo(
    () =>
      sortEntityLayersForMapSwitcher(otherEntityLayers).map((entityLayer) => ({
        label: entityLayer.name,
        mode: 'otherLayer' as const,
        entityLayerId: entityLayer.id,
      })),
    [otherEntityLayers]
  )

  const searchTypes: UniversalSearchType[] = useMemo(
    () => [
      ...allSearchTypes,
      ...recordSearchTypes,
      ...violationsSearchTypes,
      ...addressSearchTypes,
      ...planPagesSearchTypes,
      ...otherLayerSearchTypes,
    ],
    [
      addressSearchTypes,
      allSearchTypes,
      otherLayerSearchTypes,
      planPagesSearchTypes,
      recordSearchTypes,
      violationsSearchTypes,
    ]
  )

  const count = searchTypes.length

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!containerRef.current?.contains(document.activeElement)) {
        return // Don't do anything if radio does not have focus
      }

      const getSelectedIndex = () => {
        const radioItems = (containerRef.current?.querySelectorAll("[role='radio']") ??
          []) as HTMLInputElement[]
        return {
          selectedIndex: Array.from(radioItems).indexOf(e.currentTarget as HTMLInputElement),
          radioItems,
        }
      }
      switch (e.key) {
        case KeyboardKey.ArrowDown:
        case KeyboardKey.ArrowLeft:
        case KeyboardKey.ArrowRight:
        case KeyboardKey.ArrowUp:
          {
            // Move to next or previous menu item
            const direction = [KeyboardKey.ArrowDown, KeyboardKey.ArrowRight].includes(e.key)
              ? 1
              : -1

            const { selectedIndex, radioItems } = getSelectedIndex()
            if (selectedIndex < 0) {
              return
            }
            let newIndex = (selectedIndex + direction) % count
            if (newIndex < 0) {
              newIndex = count - 1
            }

            const newSearchType = searchTypes[newIndex]
            if (!newSearchType) {
              return
            }
            onSearchTypeChange(newSearchType)
            radioItems[newIndex]?.focus()
            e.preventDefault()
            e.stopPropagation()
          }
          break
      }
    },
    [count, onSearchTypeChange, searchTypes]
  )

  return (
    <StyledFlex align="center" gap="6px" wrap="wrap" role="radiogroup" ref={containerRef}>
      {searchTypes.map((st) => {
        const isChecked =
          st.mode === searchType.mode &&
          st.recordTypeId === searchType.recordTypeId &&
          st.entityLayerId === searchType.entityLayerId
        return (
          <StyledTag
            key={`${st.mode}-${st.recordTypeId ?? ''}-${st.entityLayerId ?? ''}`}
            onClick={() => onSearchTypeChange(st)}
            role="radio"
            aria-checked={isChecked}
            tabIndex={isChecked ? 0 : -1}
            onKeyDown={handleKeyDown}
            color={isChecked ? 'blue' : 'default'}
          >
            {st.label}
          </StyledTag>
        )
      })}
    </StyledFlex>
  )
}

export default React.memo(UniversalSearchTypeRadioGroup)
